import React, { createContext, useContext, useState, useEffect } from 'react'
import toast from 'react-hot-toast'

const AppContext = createContext()

export function useApp() {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useApp must be used within an AppProvider')
  }
  return context
}

export function AppProvider({ children }) {
  // Audio files state
  const [audioFiles, setAudioFiles] = useState([])
  const [bookmarks, setBookmarks] = useState([])
  const [currentAudio, setCurrentAudio] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  
  // Voice settings state
  const [voiceSettings, setVoiceSettings] = useState({
    accent: 'en-US',
    gender: 'female',
    speed: 1,
    pitch: 1,
    volume: 1,
    voice: null
  })
  
  // App settings state
  const [appSettings, setAppSettings] = useState({
    audioQuality: 'high',
    autoSave: true,
    theme: 'light',
    notifications: true
  })
  
  // Text input state
  const [textInput, setTextInput] = useState('')
  const [uploadedText, setUploadedText] = useState('')
  
  // Load data from localStorage on mount
  useEffect(() => {
    try {
      const savedAudioFiles = localStorage.getItem('voiceConverter_audioFiles')
      const savedBookmarks = localStorage.getItem('voiceConverter_bookmarks')
      const savedVoiceSettings = localStorage.getItem('voiceConverter_voiceSettings')
      const savedAppSettings = localStorage.getItem('voiceConverter_appSettings')
      
      if (savedAudioFiles) {
        setAudioFiles(JSON.parse(savedAudioFiles))
      }
      if (savedBookmarks) {
        setBookmarks(JSON.parse(savedBookmarks))
      }
      if (savedVoiceSettings) {
        setVoiceSettings(prev => ({ ...prev, ...JSON.parse(savedVoiceSettings) }))
      }
      if (savedAppSettings) {
        setAppSettings(prev => ({ ...prev, ...JSON.parse(savedAppSettings) }))
      }
    } catch (error) {
      console.error('Error loading data from localStorage:', error)
    }
  }, [])
  
  // Save data to localStorage when state changes
  useEffect(() => {
    try {
      localStorage.setItem('voiceConverter_audioFiles', JSON.stringify(audioFiles))
    } catch (error) {
      console.error('Error saving audio files:', error)
    }
  }, [audioFiles])
  
  useEffect(() => {
    try {
      localStorage.setItem('voiceConverter_bookmarks', JSON.stringify(bookmarks))
    } catch (error) {
      console.error('Error saving bookmarks:', error)
    }
  }, [bookmarks])
  
  useEffect(() => {
    try {
      localStorage.setItem('voiceConverter_voiceSettings', JSON.stringify(voiceSettings))
    } catch (error) {
      console.error('Error saving voice settings:', error)
    }
  }, [voiceSettings])
  
  useEffect(() => {
    try {
      localStorage.setItem('voiceConverter_appSettings', JSON.stringify(appSettings))
    } catch (error) {
      console.error('Error saving app settings:', error)
    }
  }, [appSettings])
  
  // Audio file operations
  const addAudioFile = (audioData) => {
    try {
      const newAudio = {
        id: Date.now(),
        title: audioData.title || `Audio ${audioFiles.length + 1}`,
        text: audioData.text,
        voiceSettings: { ...voiceSettings },
        audioUrl: audioData.audioUrl,
        duration: audioData.duration || 0,
        createdAt: new Date().toISOString(),
        size: audioData.size || 0
      }
      setAudioFiles(prev => [newAudio, ...prev])
      toast.success('Audio file saved successfully!')
      return newAudio
    } catch (error) {
      toast.error('Failed to save audio file')
      console.error(error)
    }
  }
  
  const deleteAudioFile = (id) => {
    try {
      setAudioFiles(prev => prev.filter(audio => audio.id !== id))
      if (currentAudio?.id === id) {
        setCurrentAudio(null)
        setIsPlaying(false)
      }
      toast.success('Audio file deleted successfully!')
    } catch (error) {
      toast.error('Failed to delete audio file')
      console.error(error)
    }
  }
  
  const updateAudioFile = (id, updates) => {
    try {
      setAudioFiles(prev => prev.map(audio => 
        audio.id === id ? { ...audio, ...updates } : audio
      ))
      toast.success('Audio file updated successfully!')
    } catch (error) {
      toast.error('Failed to update audio file')
      console.error(error)
    }
  }
  
  const getAudioFileById = (id) => {
    return audioFiles.find(audio => audio.id === id)
  }
  
  // Bookmark operations
  const addBookmark = (text, title) => {
    try {
      const newBookmark = {
        id: Date.now(),
        title: title || `Bookmark ${bookmarks.length + 1}`,
        text,
        createdAt: new Date().toISOString()
      }
      setBookmarks(prev => [newBookmark, ...prev])
      toast.success('Bookmark added successfully!')
      return newBookmark
    } catch (error) {
      toast.error('Failed to add bookmark')
      console.error(error)
    }
  }
  
  const deleteBookmark = (id) => {
    try {
      setBookmarks(prev => prev.filter(bookmark => bookmark.id !== id))
      toast.success('Bookmark deleted successfully!')
    } catch (error) {
      toast.error('Failed to delete bookmark')
      console.error(error)
    }
  }
  
  const updateBookmark = (id, updates) => {
    try {
      setBookmarks(prev => prev.map(bookmark => 
        bookmark.id === id ? { ...bookmark, ...updates } : bookmark
      ))
      toast.success('Bookmark updated successfully!')
    } catch (error) {
      toast.error('Failed to update bookmark')
      console.error(error)
    }
  }
  
  // Voice settings operations
  const updateVoiceSettings = (newSettings) => {
    try {
      setVoiceSettings(prev => ({ ...prev, ...newSettings }))
      if (appSettings.notifications) {
        toast.success('Voice settings updated!')
      }
    } catch (error) {
      toast.error('Failed to update voice settings')
      console.error(error)
    }
  }
  
  // App settings operations
  const updateAppSettings = (newSettings) => {
    try {
      setAppSettings(prev => ({ ...prev, ...newSettings }))
      toast.success('Settings updated successfully!')
    } catch (error) {
      toast.error('Failed to update settings')
      console.error(error)
    }
  }
  
  // Audio player operations
  const playAudio = (audioFile) => {
    try {
      setCurrentAudio(audioFile)
      setIsPlaying(true)
    } catch (error) {
      toast.error('Failed to play audio')
      console.error(error)
    }
  }
  
  const pauseAudio = () => {
    setIsPlaying(false)
  }
  
  const stopAudio = () => {
    setCurrentAudio(null)
    setIsPlaying(false)
  }
  
  // Text operations
  const updateTextInput = (text) => {
    setTextInput(text)
  }
  
  const updateUploadedText = (text) => {
    setUploadedText(text)
  }
  
  // Clear all data
  const clearAllData = () => {
    try {
      setAudioFiles([])
      setBookmarks([])
      setCurrentAudio(null)
      setIsPlaying(false)
      setTextInput('')
      setUploadedText('')
      localStorage.removeItem('voiceConverter_audioFiles')
      localStorage.removeItem('voiceConverter_bookmarks')
      toast.success('All data cleared successfully!')
    } catch (error) {
      toast.error('Failed to clear data')
      console.error(error)
    }
  }
  
  const value = {
    // State
    audioFiles,
    bookmarks,
    currentAudio,
    isPlaying,
    isLoading,
    voiceSettings,
    appSettings,
    textInput,
    uploadedText,
    
    // Audio file operations
    addAudioFile,
    deleteAudioFile,
    updateAudioFile,
    getAudioFileById,
    
    // Bookmark operations
    addBookmark,
    deleteBookmark,
    updateBookmark,
    
    // Voice settings
    updateVoiceSettings,
    
    // App settings
    updateAppSettings,
    
    // Audio player
    playAudio,
    pauseAudio,
    stopAudio,
    setIsPlaying,
    setIsLoading,
    
    // Text operations
    updateTextInput,
    updateUploadedText,
    
    // Utility
    clearAllData
  }
  
  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  )
}
